import AddIcon from '@mui/icons-material/Add';
import BorderColorIcon from '@mui/icons-material/BorderColor';
import DeleteIcon from '@mui/icons-material/Delete';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { flushSync } from 'react-dom';
import {
   ClickAwayListener,
   Grow,
   Link,
   MenuItem,
   Paper,
   Popper,
   Tooltip,
} from '@mui/material';
import { add, format, parse } from 'date-fns';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import {
   ErrorCircleButton,
   InfoCircleButton,
} from '../../../components/StyledButton';
import { CustomAdvancedMenuList, StyledDataGrid } from '../../../components/StyledComponents';
import { useLoading } from '../../../context/LoadingContext';
import AddShiftDialog from './AddShiftDialog';
import DeleteShiftDialog from './DeleteShiftDialog';
import EditShiftDialog from './EditShiftDialog';
import { useAuth } from '../../../context/AuthContext';
import { generateTranslateMuiTable } from '../../../utils/Function/commonFunction';
import CustomPagination from '../CustomPagination';
import CustomSplitButton from '../../../components/CustomSplitButton';
import ImportIcon16pxSvg from '../../../assets/images/icons/ImportIcon16pxSvg';
import ExportIcon16pxSvg from '../../../assets/images/icons/ExportIcon16pxSvg';
import { exportShiftCSV, getShiftListAll } from '../../../axios/Shift/ShiftApi';

const MODE = {
   none: -1,
   advanced: 1,
};

/** Shift giả khi tour mà chưa có shift (ngoài DEFAULT) để highlight Edit/Delete */
const TOUR_FAKE_SHIFT = {
   id: 'tour-fake-shift',
   shiftId: 'tour-fake-shift',
   shiftName: 'Sample Shift',
   shiftStart: '08:00:00',
   shiftEnd: '17:00:00',
   durationHours: '09:00:00',
};

const COLORS = [
   '#93C5FD', '#FCA5A5', '#6EE7B7', '#FB7185', '#7DD3FC',
   '#FCD34D', '#5EEAD4', '#F0ABFC', '#86EFAC', '#60A5FA',
   '#FDBA74', '#67E8F9', '#C4B5FD', '#D9F99D', '#A5B4FC',
   '#FEF08A', '#FBCFE8', '#94A3B8', '#7FFFD4', '#A1A1AA',
];

const Shifts = ({ refreshData, onRefresh }) => {
   const { t } = useTranslation();
   const { genaralSettingTimeFormat, isAdmin, isEmployee, dateTimeFormat } = useAuth();
   const [shiftsData, setShiftsData] = useState([]);
   const [page, setPage] = useState(0);
   const [pageSize, setPageSize] = useState(20);
   const [show, hide] = useLoading();
   const [mode, setMode] = useState(MODE.none);
   const anchorRefAdvanced = useRef(null);
   const tableTranslated = useMemo(() => generateTranslateMuiTable(t), [t]);

   /* =================== responsive =================== */
   const [isSm, setIsSm] = useState(false);
   useEffect(() => {
      const handleResize = () => setIsSm(window.innerWidth < 540);
      handleResize();
      window.addEventListener('resize', handleResize);
      return () => window.removeEventListener('resize', handleResize);
   }, []);

   /* =================== data =================== */
   const getColorForIndex = (index) => COLORS[index % COLORS.length];

   const columns = useMemo(() => [
      {
         field: 'shiftName',
         headerName: t('common.name'),
         flex: 1,
         renderCell: (params) => (
            <div className="flex items-center gap-2 w-full">
               <div
                  className="w-3 h-3 rounded-sm shrink-0"
                  style={{ backgroundColor: getColorForIndex(params.row.colorIndex) }}
               />
               <p className="truncate flex-1">
                  {params.value === 'DEFAULT' ? t('common.default') : params.value}
               </p>
            </div>
         ),
      },
      {
         field: 'description',
         headerName: t('common.description'),
         flex: 1.2,
         renderCell: (params) => (
            <p className="truncate line-clamp-2">
               {params.value === 'MẶC ĐỊNH' ? t('common.default') : params.value}
            </p>
         ),
      },
      {
         field: 'shiftStart',
         headerName: t('common.start'),
         flex: 0.6,
         minWidth: 100,
      },
      {
         field: 'shiftEnd',
         headerName: t('common.end'),
         flex: 0.6,
         minWidth: 100,
         renderCell: (params) => {
            const value = params.value; // ví dụ "28:00:00"
            if (!value) return '';

            const [h, m, s] = value.split(':').map(Number);

            if (h >= 24) {
               const newHour = h % 24;
               const formattedHour = String(newHour).padStart(2, '0');
               return `${formattedHour}:${m.toString().padStart(2, '0')}:${s
                  .toString()
                  .padStart(2, '0')} ${t('common.next-day')}`;
            }

            return value;
         }
      },
      {
         field: 'durationHours',
         headerName: t('common.duration'),
         flex: 0.7,
         minWidth: 120,
      },
      ...(isAdmin && !isEmployee
         ? [{
            field: 'actions',
            headerName: '',
            width: isSm ? 120 : 160,
            sortable: false,
            filterable: false,
            align: 'center',
            renderCell: (params) =>
               params.row.shiftName !== 'DEFAULT' && (
                  <div className="flex gap-1 justify-center mt-[9px]">
                     <Tooltip title={t('button.edit')} arrow>
                        <InfoCircleButton
                           data-tour="shift-and-roster-add-shift-edit-button"
                           onClick={() => {
                              if (params.row.id === 'tour-fake-shift') return;
                              handleOpenEditShift(params.row);
                           }}
                        >
                           <BorderColorIcon />
                        </InfoCircleButton>
                     </Tooltip>
                     <Tooltip title={t('button.delete')} arrow>
                        <ErrorCircleButton
                           data-tour="shift-and-roster-add-shift-delete-button"
                           className="!bg-[#FEE2E2]"
                           onClick={() => {
                              if (params.row.id === 'tour-fake-shift') return;
                              handleOpenDeleteShift(params.row);
                           }}
                        >
                           <DeleteIcon />
                        </ErrorCircleButton>
                     </Tooltip>
                  </div>
               ),
         }]
         : [])
   ], [isSm, t, isAdmin, isEmployee]);

   const getCurrentTimestamp = () => {
      const now = new Date();
      const yyyy = now.getFullYear();
      const MM = String(now.getMonth() + 1).padStart(2, '0');
      const dd = String(now.getDate()).padStart(2, '0');
      const HH = String(now.getHours()).padStart(2, '0');
      const mm = String(now.getMinutes()).padStart(2, '0');
      const ss = String(now.getSeconds()).padStart(2, '0');
      return `${yyyy}${MM}${dd}${HH}${mm}${ss}`;
   };

   const handleCloseAdvancedMenu = () => {
      setMode(MODE.none);
   };

   const exportCSV = async () => {
      const payload = {
         langId: localStorage.getItem('i18nextLngId'),
         timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
         formatDate: dateTimeFormat,
      };

      try {
         show();
         const response = await exportShiftCSV(payload);

         if (response) {
            const blob = new Blob([response.data], { type: 'application/octet-stream' });
            const url = window.URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = `${t('export.export-shift-data')}_${getCurrentTimestamp()}.xlsx`;
            document.body.appendChild(a);
            a.click();
            a.remove();
            window.URL.revokeObjectURL(url);
            // toast.success(t('common.export-shift-success'));
         }
      } catch (error) {
         if (error.response) {
            if (error.response.status !== 401) {
               toast.error(t(error?.response?.data));
            }
         } else {
            toast.error(t('common.network-timeout'));
         }
      } finally {
         hide();
         setMode(MODE.none);
      }
   };

   /* =================== fetch =================== */
   const addShiftEnd = (data) => {
      const newData = { ...data };
      if (!newData.shiftEnd) {
         const shiftStart = parse(newData.shiftStart, 'HH:mm:ss', new Date());
         const [h, m, s] = newData.durationHours.split(':').map(Number);
         const shiftEnd = add(shiftStart, { hours: h, minutes: m, seconds: s });
         newData.shiftEnd = format(shiftEnd, 'HH:mm:ss');
      }
      return newData;
   };

   const fetchData = async () => {
      try {
         show();
         const res = await getShiftListAll(localStorage.getItem('i18nextLngId'));
         if (res?.data?.listShift) {
            setShiftsData(res.data.listShift.map(addShiftEnd));
         }
      } catch (e) {
         toast.error(t('common.error'));
      } finally {
         hide();
      }
   };

   useEffect(() => {
      fetchData();
   }, [refreshData]);

   /* =================== dialogs =================== */
   const [openAddShift, setOpenAddShift] = useState(false);
   const [openEditShift, setOpenEditShift] = useState(false);
   const [openDeleteShift, setOpenDeleteShift] = useState(false);
   const [selectedShift, setSelectedShift] = useState({});
   // Tour: fake shift khi chưa có shift thật (ngoài DEFAULT)
   const [tourFakeShift, setTourFakeShift] = useState(false);

   useEffect(() => {
      window.__openAddShiftDialog = () => {
         flushSync(() => {
            setMode(MODE.none);
            setOpenAddShift(true);
         });
      };
      window.__closeAddShiftDialog = () => {
         flushSync(() => setOpenAddShift(false));
      };
      window.__enableTourFakeShift = () => {
         flushSync(() => {
            setOpenAddShift(false);
            setTourFakeShift(true);
         });
      };
      window.__disableTourFakeShift = () => {
         flushSync(() => setTourFakeShift(false));
      };
      return () => {
         delete window.__openAddShiftDialog;
         delete window.__closeAddShiftDialog;
         delete window.__enableTourFakeShift;
         delete window.__disableTourFakeShift;
      };
   }, []);

   const handleOpenEditShift = (shift) => {
      setSelectedShift(shift);
      setOpenEditShift(true);
   };
   const handleOpenDeleteShift = (shift) => {
      setSelectedShift(shift);
      setOpenDeleteShift(true);
   };

   const hasEditableShift = useMemo(
      () => shiftsData.some((s) => s.shiftName !== 'DEFAULT'),
      [shiftsData]
   );

   const displayShifts = useMemo(() => {
      if (hasEditableShift || !tourFakeShift) return shiftsData;
      return [...shiftsData, TOUR_FAKE_SHIFT];
   }, [shiftsData, hasEditableShift, tourFakeShift]);

   const rows = useMemo(
      () =>
         displayShifts.map((item, index) => ({
            ...item,
            id: item.id ?? item.shiftId ?? index,
            colorIndex: index,
         })),
      [displayShifts]
   );

   function formatTime(input, formatStr) {
      const [h, m, s] = input.split(':');
      return formatStr
         .replace('HH', h)
         .replace('mm', m)
         .replace('ss', s);
   }

   function displayRoundedDuration(duration) {
      const [h, m, s] = duration.split(':').map(Number);
      return `${m > 0 || s > 0 ? h + 1 : h} ${t('common.hours')}`;
   }

   const handleRowsPerPageChange = (newPageSize) => {
      const size =
         typeof newPageSize === 'number'
            ? newPageSize
            : parseInt(newPageSize.target?.value ?? newPageSize, 10);

      setPage((prevPage) => {
         const firstRowIndex = prevPage * pageSize;
         const newPage = Math.floor(firstRowIndex / size);
         const maxPage = Math.max(0, Math.ceil(rows.length / size) - 1);
         return Math.min(newPage, maxPage);
      });

      setPageSize(size);
   };

   const hasRows = rows.length > 0;
   const gridHeight = hasRows ? '68vh' : 360;

   /* =================== render =================== */
   return (
      <div className="shifts">
         {/* toolbar */}
         {isAdmin && !isEmployee && (
            <div
               className="flex justify-end gap-3 flex-wrap my-1 mb-4"
            >
               <CustomSplitButton
                  label={t('common.add-shift')}
                  startIcon={<AddIcon />}
                  onMainClick={() => setOpenAddShift(true)}
                  onDropdownClick={() => setMode(MODE.advanced)}
                  ref={anchorRefAdvanced}
                  data-tour="shift-and-roster-add-shift-button"
               />
            </div>
         )}

         {/* grid */}
         <div className="w-full overflow-x-auto" data-tour="shift-and-roster-add-shift-table">
            <StyledDataGrid
               rows={rows}
               columns={columns}
               getRowId={(row) => row.id}
               rowCount={rows.length}
               paginationMode="client"
               disableRowSelectionOnClick
               localeText={{
                  ...tableTranslated,
                  noRowsLabel: t('common.no-shifts-available'),
               }}
               slots={{
                  pagination: CustomPagination,
               }}
               slotProps={{
                  pagination: {
                     page,
                     count: rows.length,
                     rowsPerPage: pageSize,
                     onPageChange: setPage,
                     onRowsPerPageChange: handleRowsPerPageChange,
                  },
               }}
               paginationModel={{
                  page,
                  pageSize,
               }}
               onPaginationModelChange={(model) => {
                  if (model.page !== page) setPage(model.page);
                  if (model.pageSize !== pageSize) setPageSize(model.pageSize);
               }}
               sx={{
                  height: gridHeight,
                  border: 0,

                  '& .MuiDataGrid-scrollbar--vertical, & .MuiDataGrid-scrollbar--horizontal': {
                     display: hasRows ? 'none !important' : 'none !important',
                  },
                  '& [class*="MuiDataGrid-scrollbar"][class*="--horizontal"]': {
                     display: 'none !important',
                  },
                  '& .MuiDataGrid-virtualScroller': {
                     overflow: hasRows ? 'auto !important' : 'hidden !important',
                  },
               }}
            />
         </div>


         {/* dialogs */}
         <AddShiftDialog open={openAddShift} handleClose={() => setOpenAddShift(false)} onRefresh={onRefresh} />
         <EditShiftDialog
            open={openEditShift}
            handleClose={() => setOpenEditShift(false)}
            selectedEditShift={selectedShift}
            onRefresh={onRefresh}
         />
         <DeleteShiftDialog
            open={openDeleteShift}
            handleClose={() => setOpenDeleteShift(false)}
            selectedDeleteShift={selectedShift}
            onRefresh={onRefresh}
         />

         {mode === MODE.advanced && (
            <Popper
               open
               anchorEl={anchorRefAdvanced.current}
               placement="bottom-start"
               transition
               disablePortal
               className="z-10"
            >
               {({ TransitionProps, placement }) => (
                  <Grow
                     {...TransitionProps}
                     style={{
                        transformOrigin:
                           placement === 'bottom' ? 'left top' : 'left bottom',
                     }}
                  >
                     <Paper style={{ borderRadius: '6px' }}>
                        <ClickAwayListener onClickAway={handleCloseAdvancedMenu}>
                           <CustomAdvancedMenuList
                              id="shift-advanced-menu"
                              aria-labelledby="shift-advanced-button"
                           >
                              <MenuItem
                                 disabled
                                 style={{
                                    borderBottom: '1px solid #E5E7EB',
                                    opacity: '0.5',
                                    pointerEvents: 'none',
                                 }}
                              >
                                 <Link>
                                    <ImportIcon16pxSvg />
                                    <p>{t('button.import-data')}</p>
                                 </Link>
                              </MenuItem>
                              <MenuItem
                                 style={{
                                    borderBottom: '1px solid #E5E7EB',
                                    pointerEvents: 'auto',
                                 }}
                                 onClick={exportCSV}
                              >
                                 <Link>
                                    <ExportIcon16pxSvg />
                                    <p>{t('button.export-data')}</p>
                                 </Link>
                              </MenuItem>
                           </CustomAdvancedMenuList>
                        </ClickAwayListener>
                     </Paper>
                  </Grow>
               )}
            </Popper>
         )}
      </div>
   );
};

export default Shifts;